import { DOCUMENT } from "@angular/common";
import { inject, Injectable, signal } from "@angular/core";

export type ThemeMode = "new-look" | "softvaerket" | "dark";

export interface ThemeOption {
    mode: ThemeMode;
    label: string;
}

const STORAGE_KEY = "servicepro-theme";
const DARK_CLASS = "dark-theme";
const SOFTVAERKET_CLASS = "softvaerket-theme";
const TRANSITION_CLASS = "theme-transition";
const THEME_TRANSITION_MS = 1200;

const VALID_THEMES: ThemeMode[] = ["new-look", "softvaerket", "dark"];

@Injectable({ providedIn: "root" })
export class ThemeService {
    private readonly document = inject(DOCUMENT);
    public readonly activeTheme = signal<ThemeMode>(this.resolveInitialTheme());
    private themeTransitionTimeoutId: number | null = null;

    constructor() {
        this.resetTransitionState();
        this.applyTheme(this.activeTheme(), false);
    }

    /** New Look kun lokalt (localhost); ellers Lys + Mørk. */
    public getThemeOptions(): ThemeOption[] {
        const options: ThemeOption[] = [
            { mode: "softvaerket", label: "Lys" },
            { mode: "dark", label: "Mørk" }
        ];
        if (this.isNewLookAvailable()) {
            return [{ mode: "new-look", label: "New Look" }, ...options];
        }
        return options;
    }

    public isNewLookAvailable(): boolean {
        return this.isLocalHost();
    }

    public setTheme(mode: ThemeMode, withTransition: boolean = false): void {
        const resolved = this.resolveAllowedTheme(mode);
        this.activeTheme.set(resolved);
        this.applyTheme(resolved, withTransition);
        try {
            localStorage.setItem(STORAGE_KEY, resolved);
        } catch {
            // localStorage unavailable (e.g. incognito with storage disabled)
        }
    }

    private applyTheme(mode: ThemeMode, withTransition: boolean): void {
        const body = this.document.body;
        if (withTransition) {
            this.applyTemporaryTransitionClass(body);
        } else {
            this.resetTransitionState();
        }

        body.classList.remove(DARK_CLASS, SOFTVAERKET_CLASS);

        if (mode === "dark") {
            body.classList.add(DARK_CLASS);
        } else if (mode === "softvaerket") {
            body.classList.add(SOFTVAERKET_CLASS);
        }
    }

    private applyTemporaryTransitionClass(body: HTMLElement): void {
        const win = this.document.defaultView;
        if (!win) return;

        body.classList.add(TRANSITION_CLASS);

        if (this.themeTransitionTimeoutId !== null) {
            win.clearTimeout(this.themeTransitionTimeoutId);
        }

        this.themeTransitionTimeoutId = win.setTimeout(() => {
            body.classList.remove(TRANSITION_CLASS);
            this.themeTransitionTimeoutId = null;
        }, THEME_TRANSITION_MS);
    }

    private resetTransitionState(): void {
        const body = this.document.body;
        body.classList.remove(TRANSITION_CLASS);

        const win = this.document.defaultView;
        if (!win || this.themeTransitionTimeoutId === null) return;

        win.clearTimeout(this.themeTransitionTimeoutId);
        this.themeTransitionTimeoutId = null;
    }

    private resolveInitialTheme(): ThemeMode {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                if (stored === "light") {
                    return this.resolveAllowedTheme("new-look");
                }
                if (VALID_THEMES.includes(stored as ThemeMode)) {
                    return this.resolveAllowedTheme(stored as ThemeMode);
                }
            }
        } catch {
            // ignore
        }
        // Lokalt: New Look som default. Ellers Lys (softvaerket).
        return this.isNewLookAvailable() ? "new-look" : "softvaerket";
    }

    private resolveAllowedTheme(mode: ThemeMode): ThemeMode {
        if (mode === "new-look" && !this.isNewLookAvailable()) {
            return "softvaerket";
        }
        return mode;
    }

    private isLocalHost(): boolean {
        const host = this.document.defaultView?.location?.hostname ?? "";
        return host === "localhost" || host === "127.0.0.1";
    }
}
